import { FC, useState, useEffect } from 'react'
import { Calendar, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { Button } from '../../../../components/Button/Button'
import { Input } from '../../../../components/Input/Input'
import { POST, DELETE, GET } from '../../../../services/api'
import Swal from 'sweetalert2'
import styles from '../config.module.css'

type HolidayType = {
  id: number
  bookingDayId: number
  day?: string // Dia da semana (sunday, monday, etc) - vindo da API
  holidayDate: string
  isRecurringYearly: boolean
  createdAt: string
  updatedAt: string
  holidayName?: string // Nome do feriado
  dayName?: string // Nome traduzido do dia da semana (Domingo, Segunda, etc)
}

type BookingDayWithIdType = {
  id: number
  day: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
  able: boolean
}

const dayNameMap: {
  [key: string]: string
} = {
  sunday: 'Domingo',
  monday: 'Segunda',
  tuesday: 'Terça',
  wednesday: 'Quarta',
  thursday: 'Quinta',
  friday: 'Sexta',
  saturday: 'Sábado',
}

// Ordem dos dias da semana para exibição no select
const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

interface HolidayManagerProps {
  bookingDays?: BookingDayWithIdType[]
  onHolidaysChange?: (holidays: HolidayType[]) => void
}

export const HolidayManager: FC<HolidayManagerProps> = ({
  bookingDays = [],
  onHolidaysChange,
}) => {
  const [holidays, setHolidays] = useState<HolidayType[]>([])
  const [isExpanded, setIsExpanded] = useState(false)
  const [loading, setLoading] = useState(false)

  // Form states
  const [selectedDayId, setSelectedDayId] = useState<number | null>(null)
  const [isRecurring, setIsRecurring] = useState(true)
  const [holidayDate, setHolidayDate] = useState('')
  const [holidayName, setHolidayName] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  // Load holidays on mount
  useEffect(() => {
    loadHolidays()
  }, [])

  const loadHolidays = async () => {
    try {
      setLoading(true)
      const res = await GET('/queueReservation/bookingConfig/holidays')
      if (res && res.data && Array.isArray(res.data)) {
        const enrichedHolidays = res.data.map((h: HolidayType) => ({
          ...h,
          dayName: h.day ? dayNameMap[h.day] || h.day : 'Desconhecido',
        }))
        setHolidays(enrichedHolidays)
        onHolidaysChange?.(enrichedHolidays)
      }
    } catch (error) {
      console.error('Erro ao carregar feriados:', error)
    } finally {
      setLoading(false)
    }
  }

  const findDayName = (dayId: number): string => {
    const day = bookingDays.find((d) => d.id === dayId)
    return day ? dayNameMap[day.day] || day.day : 'Desconhecido'
  }

  const getSortedBookingDays = (): BookingDayWithIdType[] => {
    return bookingDays.sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day))
  }

  const validateHolidayDate = (): boolean => {
    if (!selectedDayId) {
      setFormError('Selecione um dia da semana')
      return false
    }

    if (!holidayDate) {
      setFormError('Selecione uma data')
      return false
    }

    if (isRecurring) {
      // Validar formato MM-DD
      const mmddRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/
      if (!mmddRegex.test(holidayDate)) {
        setFormError('Formato inválido. Use MM-DD (ex: 05-01 para 1º de maio)')
        return false
      }
    } else {
      // Validar formato YYYY-MM-DD
      const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/
      if (!dateRegex.test(holidayDate)) {
        setFormError('Formato inválido. Use YYYY-MM-DD (ex: 2026-04-05)')
        return false
      }
    }

    setFormError(null)
    return true
  }

  const handleAddHoliday = async () => {
    if (!validateHolidayDate()) {
      return
    }

    try {
      setLoading(true)
      const res = await POST('/queueReservation/bookingConfig/holidays', {
        bookingDayId: selectedDayId,
        holidayDate,
        isRecurringYearly: isRecurring,
        holidayName: holidayName || null,
      })

      if (res && res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso',
          text: 'Feriado adicionado com sucesso',
          confirmButtonText: 'OK',
        })
        // Reset form
        setSelectedDayId(null)
        setHolidayDate('')
        setHolidayName('')
        setIsRecurring(true)
        setFormError(null)
        // Reload holidays
        await loadHolidays()
      }
    } catch (error: any) {
      let message = 'Erro ao adicionar feriado'
      if (error.response?.data?.message) {
        message = error.response.data.message
      }
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: message,
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteHoliday = async (holidayId: number) => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Confirmar exclusão',
      text: 'Tem certeza que deseja remover este feriado?',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
    })

    if (!confirm.isConfirmed) {
      return
    }

    try {
      setLoading(true)
      const res = await DELETE(
        `/queueReservation/bookingConfig/holidays/${holidayId}`
      )

      if (res && res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso',
          text: 'Feriado removido com sucesso',
          confirmButtonText: 'OK',
        })
        await loadHolidays()
      }
    } catch (error: any) {
      let message = 'Erro ao remover feriado'
      if (error.response?.data?.message) {
        message = error.response.data.message
      }
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: message,
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }

  const formatHolidayDate = (date: string, isRecurring: boolean): string => {
    if (isRecurring) {
      // MM-DD format
      const [month, day] = date.split('-')
      return `${day}/${month} (todo ano)`
    } else {
      // YYYY-MM-DD format
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    }
  }

  return (
    <div style={{ marginTop: '30px', marginBottom: '20px' }}>
      {/* Toggle section */}
      <div
        style={{
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: '1px solid #dee2e6',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Calendar size={15} />
          <h5 style={{ margin: 0 }}>Gerenciar Feriados</h5>
        </span>
        <span>
          {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </span>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div
          style={{
            padding: '20px',
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
          }}
        >
          {/* Add holiday form */}
          <div style={{ marginBottom: '30px' }}>
            <h6>Adicionar novo feriado</h6>

            {/* Day selection */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Dia da semana *
              </label>
              <select
                value={selectedDayId || ''}
                onChange={(e) => setSelectedDayId(e.target.value ? parseInt(e.target.value) : null)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: 'inherit',
                }}
              >
                <option value="">Selecione um dia...</option>
                {getSortedBookingDays().map((day) => (
                  <option key={day.id} value={day.id}>
                    {dayNameMap[day.day]}
                  </option>
                ))}
              </select>
            </div>

            {/* Holiday name input */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Nome do Feriado (opcional)
              </label>
              <Input
                inputAttr={{ type: 'text' }}
                placeholder="Ex: Carnaval, Corpus Christi, Festa de Santo Antônio"
                value={holidayName}
                change={(value: string) => setHolidayName(value)}
                disabled={loading}
              />
            </div>

            {/* Type toggle */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Tipo de feriado *
              </label>
              <div style={{ display: 'flex', gap: '15px' }}>
                <label>
                  <input
                    type="radio"
                    checked={isRecurring}
                    onChange={() => setIsRecurring(true)}
                  />
                  {' '}Fixo (todo ano)
                </label>
                <label>
                  <input
                    type="radio"
                    checked={!isRecurring}
                    onChange={() => setIsRecurring(false)}
                  />
                  {' '}Variável (ano específico)
                </label>
              </div>
            </div>

            {/* Date input */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {isRecurring ? 'Data (MM-DD)' : 'Data (YYYY-MM-DD)'} *
              </label>
              <Input
                inputAttr={{ type: 'text' }}
                placeholder={isRecurring ? 'Ex: 05-01' : 'Ex: 2026-04-05'}
                value={holidayDate}
                change={(value: string) => setHolidayDate(value)}
                disabled={loading}
              />
              {formError && (
                <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                  {formError}
                </p>
              )}
            </div>

            {/* Add button */}
            <Button
              click={handleAddHoliday}
              disabled={loading}
              text={loading ? 'Adicionando...' : '+ Adicionar feriado'}
              color="primary"
            />
          </div>

          {/* Holidays list */}
          <div>
            <h6>Feriados cadastrados ({holidays.length})</h6>
            {holidays.length === 0 ? (
              <p style={{ color: '#999', fontStyle: 'italic' }}>
                Nenhum feriado cadastrado
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ textAlign: 'left', padding: '10px' }}>Dia da Semana</th>
                    <th style={{ textAlign: 'left', padding: '10px' }}>Nome do Feriado</th>
                    <th style={{ textAlign: 'left', padding: '10px' }}>Data</th>
                    <th style={{ textAlign: 'left', padding: '10px' }}>Tipo</th>
                    <th style={{ textAlign: 'center', padding: '10px' }}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map((holiday) => (
                    <tr key={holiday.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px' }}>{holiday.dayName}</td>
                      <td style={{ padding: '10px' }}>
                        <span style={{ fontWeight: 500, color: '#333' }}>
                          {holiday.holidayName || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '10px' }}>
                        {formatHolidayDate(holiday.holidayDate, holiday.isRecurringYearly)}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: holiday.isRecurringYearly
                              ? '#d4edda'
                              : '#cfe2ff',
                            color: holiday.isRecurringYearly ? '#155724' : '#084298',
                          }}
                        >
                          {holiday.isRecurringYearly ? 'Fixo' : 'Variável'}
                        </span>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          disabled={loading}
                          title="Remover feriado"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            margin: '0 auto',
                          }}
                        >
                          <Trash2 size={16} />
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
